import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import Overlay from "components/Overlay/Overlay";
import { useStateContext } from "contexts/ContextProvider";
import { validateQuietHoursAgainstBusinessHours } from "helpers/quietHoursBusinessHours";
import { usePageTitle } from "hooks/usePageTitle";
import linguistApi from "services/linguistApi";

const QUIET_HOURS_START_STORAGE_KEY = "ce_portal.quietHoursStart";
const QUIET_HOURS_END_STORAGE_KEY = "ce_portal.quietHoursEnd";
const TIMEZONE_STORAGE_KEY = "ce_portal.notificationTimezone";

const DEFAULT_QUIET_START = "22:00";
const DEFAULT_QUIET_END = "07:00";

const formatTime12Hour = (timeValue) => {
  const normalized = String(timeValue || "").trim();
  const [rawHour, rawMinute] = normalized.split(":");
  const hour = Number(rawHour);
  const minute = Number(rawMinute);

  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    return normalized || "N/A";
  }

  const safeHour = ((hour % 24) + 24) % 24;
  const period = safeHour >= 12 ? "PM" : "AM";
  const hour12 = safeHour % 12 === 0 ? 12 : safeHour % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
};

const buildTimeOptions = (stepMinutes = 15) => {
  const options = [];

  for (let totalMinutes = 0; totalMinutes < 24 * 60; totalMinutes += stepMinutes) {
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    options.push({ value, label: formatTime12Hour(value) });
  }

  return options;
};

const TIMEZONE_OPTIONS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "UTC",
];

const getDefaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch (error) {
    return "America/New_York";
  }
};

const LinguistNotificationSettingsEditOverlay = () => {
  usePageTitle("Notification Settings");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar, triggerPageRefresh } = useStateContext();

  const [quietHoursStart, setQuietHoursStart] = useState(
    overlayData?.settings?.QuietHoursStart || DEFAULT_QUIET_START,
  );
  const [quietHoursEnd, setQuietHoursEnd] = useState(
    overlayData?.settings?.QuietHoursEnd || DEFAULT_QUIET_END,
  );
  const [timezone, setTimezone] = useState(
    overlayData?.settings?.Timezone || getDefaultTimezone(),
  );
  const [isSaving, setIsSaving] = useState(false);

  const timeOptions = useMemo(() => {
    const baseOptions = buildTimeOptions(15);
    const optionMap = new Map(baseOptions.map((option) => [option.value, option]));

    [quietHoursStart, quietHoursEnd].forEach((timeValue) => {
      const normalized = String(timeValue || "").trim();
      if (!normalized || optionMap.has(normalized)) {
        return;
      }

      optionMap.set(normalized, {
        value: normalized,
        label: formatTime12Hour(normalized),
      });
    });

    return [...optionMap.values()];
  }, [quietHoursStart, quietHoursEnd]);

  const timezoneOptions = useMemo(() => {
    if (!timezone || TIMEZONE_OPTIONS.includes(timezone)) {
      return TIMEZONE_OPTIONS;
    }

    return [...TIMEZONE_OPTIONS, timezone];
  }, [timezone]);

  const quietHoursValidation = useMemo(
    () =>
      validateQuietHoursAgainstBusinessHours({
        start: quietHoursStart,
        end: quietHoursEnd,
        timezone,
      }),
    [quietHoursStart, quietHoursEnd, timezone],
  );

  const quietHoursError =
    quietHoursStart && quietHoursEnd && !quietHoursValidation.valid
      ? quietHoursValidation.reason
      : "";

  const canSave = useMemo(
    () =>
      Boolean(
        quietHoursStart &&
          quietHoursEnd &&
          timezone &&
          quietHoursValidation.valid,
      ),
    [quietHoursStart, quietHoursEnd, timezone, quietHoursValidation],
  );

  const closeOverlay = () => setOverlayName("");

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    const validation = validateQuietHoursAgainstBusinessHours({
      start: quietHoursStart,
      end: quietHoursEnd,
      timezone,
    });

    if (!validation.valid) {
      showSnackbar(validation.reason, theme, "error");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        NotificationSettings: {
          QuietHoursStart: quietHoursStart || DEFAULT_QUIET_START,
          QuietHoursEnd: quietHoursEnd || DEFAULT_QUIET_END,
          Timezone: timezone || getDefaultTimezone(),
        },
      };

      await linguistApi.put("/me", payload);

      window.localStorage.setItem(
        QUIET_HOURS_START_STORAGE_KEY,
        quietHoursStart || DEFAULT_QUIET_START,
      );
      window.localStorage.setItem(
        QUIET_HOURS_END_STORAGE_KEY,
        quietHoursEnd || DEFAULT_QUIET_END,
      );
      window.localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone || getDefaultTimezone());

      triggerPageRefresh("Notification settings updated", theme);
      closeOverlay();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.error || "Unable to save notification settings",
        theme,
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const footer = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        width: "100%",
        flexDirection: "row",
      }}
    >
      {isMobile ? (
        <Tooltip title="Discard" placement="top">
          <IconButton
            color="primary"
            onClick={closeOverlay}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          variant="outlined"
          size="large"
          endIcon={<DisabledByDefaultRoundedIcon />}
          onClick={closeOverlay}
          sx={{
            borderRadius: "12px",
          }}
        >
          Discard
        </Button>
      )}

      <Box p="0 .5rem" />

      {isMobile ? (
        <Tooltip title={isSaving ? "Saving..." : "Save"} placement="top">
          <span>
            <IconButton
              disabled={!canSave || isSaving}
              color="primary"
              onClick={handleSave}
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              <SaveRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          loading={isSaving}
          disabled={!canSave || isSaving}
          color="primary"
          variant="contained"
          size="large"
          endIcon={<SaveRoundedIcon />}
          onClick={handleSave}
          sx={{
            borderRadius: "12px",
          }}
        >
          Save
        </Button>
      )}
    </Box>
  );

  return (
    <Overlay
      id="linguist-notification-settings-edit"
      open={true}
      onClose={closeOverlay}
      title="Edit Notification Settings"
      height="fit-content"
      footer={footer}
    >
      <Box
        component="form"
        sx={{
          display: "flex",
          p: "1rem 2.3rem 1rem 2.5rem",
          width: "100%",
          "& .MuiTextField-root": {
            m: 0,
            width: "100%",
          },
        }}
      >
        <Grid container spacing={2} width="100%">
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "10px" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Quiet Hours
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                Notifications are silenced during this window.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Quiet hours start"
                  value={quietHoursStart}
                  onChange={(event) => setQuietHoursStart(event.target.value)}
                  error={Boolean(quietHoursError)}
                  fullWidth
                >
                  {timeOptions.map((option) => (
                    <MenuItem key={`quiet-start-${option.value}`} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Quiet hours end"
                  value={quietHoursEnd}
                  onChange={(event) => setQuietHoursEnd(event.target.value)}
                  error={Boolean(quietHoursError)}
                  helperText={quietHoursError || " "}
                  fullWidth
                >
                  {timeOptions.map((option) => (
                    <MenuItem key={`quiet-end-${option.value}`} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mt: 1 }}
              >
                Quiet hours must stay outside business hours: Mon–Fri 7:30 AM – 6:00 PM ET.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "10px" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Timezone
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                Used for quiet hours and notification timing.
              </Typography>
              <TextField
                select
                size="small"
                label="Notification timezone"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                fullWidth
              >
                {timezoneOptions.map((timezoneOption) => (
                  <MenuItem key={timezoneOption} value={timezoneOption}>
                    {timezoneOption}
                  </MenuItem>
                ))}
              </TextField>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Overlay>
  );
};

export default LinguistNotificationSettingsEditOverlay;
